<style scoped>
.badge-larvae {
	background-color: #ffc107;
	color: #000;
}

.badge-pupae {
	background-color: #fd7e14;
	color: #fff;
}

.badge-adult {
	background-color: #dc3545;
	color: #fff;
}
.details-section {
	overflow-y: auto;
}
.icon-standing-water {
	color: #0dcaf0;
}

.icon-nuisance {
	color: #dc3545;
}
.note-body {
	white-space: pre-wrap;
}
.service-request-grid {
	display: grid;
	grid-template-columns: auto 1fr;
	column-gap: 0.75rem;
	row-gap: 0.15rem;
	align-items: baseline;
}
.map-container {
	background-color: #e9ecef;
	border-radius: 10px;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
	height: 400px;
	margin-top: 20px;
	position: relative;
}
</style>

<template>
	<div class="card shadow-sm mb-3">
		<div class="card-header bg-white pane-header">Communication Workbench</div>
		<div class="card-body">
			<div class="map-container">
				<Map ref="mapRef" :bounds="mapBounds" :markers="mapMarkers">
					<Layer
						id="parcel"
						:minzoom="14"
						:paint="{ 'line-color': '#0f0' }"
						source="tegola"
						sourceLayer="parcel"
						type="line"
					/>
					<Layer
						id="service-area"
						:paint="{ 'line-color': '#f00' }"
						source="tegola"
						sourceLayer="service-area-bounds"
						type="line"
					/>
					<!-- Selection outline for the communication's resolved parcel.
					     Always mounted; the filter (never-match fallback) drives it. -->
					<Layer
						id="parcel-selected"
						:minzoom="14"
						:filter="parcelSelectionFilter"
						:paint="{
							'line-color': '#00e5ff',
							'line-width': 3,
							'line-opacity': 1,
						}"
						source="tegola"
						sourceLayer="parcel"
						type="line"
					/>
					<Source
						id="tegola"
						type="vector"
						:tiles="[session.urls?.tegola + 'maps/nidus/{z}/{x}/{y}']"
					/>
				</Map>
			</div>
			<CommunicationPropertyCard
				v-if="selectedCommunication"
				:property="property"
				:propertyError="propertyError"
				:loading="propertyLoading"
				:reportAddress="selectedCommunication.address_raw"
				@zoom="$emit('zoomProperty', $event)"
			/>
			<div
				v-if="!selectedCommunication"
				class="d-flex flex-column align-items-center justify-content-center text-muted"
			>
				<i class="bi bi-hand-index fs-1"></i>
				<p class="mt-2">Select a report to view details</p>
			</div>

			<div class="h-100 d-flex flex-column" v-else>
				<div v-if="statusBadge" class="card shadow-sm mb-3">
					<div class="card-body">
						<span
							class="badge rounded-pill fs-5"
							:class="`text-bg-${statusBadge.variant}`"
						>
							<i class="bi me-1" :class="statusBadge.icon"></i
							>{{ statusBadge.label }}
						</span>
						<div
							v-if="selectedCommunication.status_update.user"
							class="small mt-2"
						>
							Changed by {{ selectedCommunication.status_update.user }}
						</div>
						<div class="small text-muted mt-1">
							{{ formatDateTime(selectedCommunication.status_update.created) }}
						</div>
					</div>
				</div>
				<div class="card shadow-sm mb-3">
					<div class="card-body">
						<h6 class="text-muted small mb-2">
							<i class="bi bi-person me-1"></i> Contact
						</h6>
						<div class="fw-medium">
							{{ selectedCommunication.contact.name || "not given" }}
						</div>
						<div
							v-if="selectedCommunication.contact.emails.length > 0"
							class="small"
						>
							<i class="bi bi-envelope me-1"></i>
							<a
								v-for="email in selectedCommunication.contact.emails"
								:key="email"
								:href="'mailto:' + email"
								>{{ email }}</a
							>
						</div>
						<div
							v-if="selectedCommunication.contact.phones.length > 0"
							class="small"
						>
							<i class="bi bi-phone me-1"></i>
							<a
								v-for="phone in selectedCommunication.contact.phones"
								:key="phone.e164"
								:href="'tel:' + phone.e164"
								>{{ phone.e164 }}</a
							>
						</div>
						<div v-if="selectedCommunication.address_raw" class="small mt-1">
							<i class="bi bi-geo-alt me-1"></i>
							{{ selectedCommunication.address_raw }}
						</div>
					</div>
				</div>
				<div v-if="serviceRequest" class="card shadow-sm mb-3">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-center mb-2">
							<h6 class="text-muted small mb-0">
								<i class="bi bi-clipboard-check me-1"></i> Service Request
							</h6>
							<button
								v-if="serviceRequest.location"
								type="button"
								class="btn btn-sm btn-outline-secondary"
								title="Center map on location"
								aria-label="Center map on location"
								@click="centerOnServiceRequest"
							>
								<i class="bi bi-crosshair"></i>
							</button>
						</div>
						<div class="service-request-grid small">
							<template
								v-for="field in serviceRequestFields"
								:key="field.label"
							>
								<div class="text-muted">{{ field.label }}</div>
								<div>
									<a v-if="field.href" :href="field.href">{{ field.value }}</a>
									<span v-else>{{ field.value }}</span>
								</div>
							</template>
						</div>
						<div
							v-for="note in serviceRequestNotes"
							:key="note.label"
							class="small mt-2"
						>
							<div class="text-muted">{{ note.label }}</div>
							<div class="note-body">{{ note.value }}</div>
						</div>
					</div>
				</div>
				<div v-if="selectedCommunication.body" class="card shadow-sm mb-3">
					<div class="card-body note-body">
						{{ selectedCommunication.body }}
					</div>
				</div>
				<CardResource
					@viewImage="openPhotoViewer"
					:resource="resource"
					:transferredFrom="selectedCommunication.transferred_from"
					v-for="(resource, index) in resourceByCreated(selectedCommunication)"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { FilterSpecification } from "maplibre-gl";

import CardResource from "@/components/CardResource.vue";
import CommunicationPropertyCard from "@/components/CommunicationPropertyCard.vue";
import TimeRelative from "@/components/TimeRelative.vue";
import { communicationStatusBadge } from "@/components/communicationStatus";
import { formatDate as formatDateTime } from "@/format";
import Map, { LngLatBounds } from "@/map/Map.vue";
import Layer from "@/map/Layer.vue";
import Source from "@/map/Source.vue";
import { useSessionStore } from "@/store/session";
import { useStoreResource } from "@/store/resource";
import type { Marker } from "@/types";
import type {
	Bounds,
	Communication,
	CommunicationProperty,
	FieldseekerServiceRequest,
	Message,
	PublicReport,
	ResourceStub,
	User,
} from "@/type/api";

interface Emits {
	(e: "viewImage", uuid: string): void;
	(e: "zoomProperty", bounds: LngLatBounds): void;
}
interface Props {
	loading: boolean;
	mapBounds?: LngLatBounds;
	mapMarkers: Marker[];
	property: CommunicationProperty | null;
	propertyError: string;
	propertyLoading: boolean;
	selectedCommunication: Communication | undefined;
}
interface ServiceRequestField {
	href?: string;
	label: string;
	value: string;
}
interface ServiceRequestNote {
	label: string;
	value: string;
}

const emit = defineEmits<Emits>();
const props = defineProps<Props>();
const session = useSessionStore();
const storeResource = useStoreResource();
const mapRef = ref<InstanceType<typeof Map> | null>(null);

// Highlight the resolved parcel on the map, keyed on its assessor id (the
// parcel MVT carries external_id). The never-match fallback ("") keeps the
// expression valid when nothing is selected: the always-mounted layer simply
// never matches.
const parcelSelectionFilter = computed((): FilterSpecification => {
	const externalID = props.property?.parcel?.external_id ?? "";
	// The expression form is valid maplibre filter syntax, but the generated
	// FilterSpecification union does not model a bare ["==", ["get", ...], v]
	// triple, so it has to be cast once here.
	return [
		"==",
		["get", "external_id"],
		externalID,
	] as unknown as FilterSpecification;
});

// The status card is shown for finished work only: an open communication's
// status needs no explanation, so the record starts with the Contact card as it
// always has.
const statusBadge = computed(() =>
	props.selectedCommunication
		? communicationStatusBadge(props.selectedCommunication.status)
		: null,
);

function openPhotoViewer(uuid: string) {
	emit("viewImage", uuid);
}
// The mirrored FieldSeeker service request a communication was imported from,
// keyed by the resource URI carried as the communication's source. Absent for
// communications that did not come from FieldSeeker.
const serviceRequest = computed<FieldseekerServiceRequest | null>(() => {
	return (
		storeResource.servicerequest.byURI.get(
			props.selectedCommunication?.source ?? "",
		) ?? null
	);
});
// centerOnServiceRequest moves the workbench map to the imported service
// request's point, for the icon button in the Service Request card header.
function centerOnServiceRequest() {
	const location = serviceRequest.value?.location;
	if (!location) return;
	mapRef.value?.centerOn(location);
}
function formatDate(value: Date | string | null | undefined): string | null {
	if (value == null || value === "") return null;
	const date = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(date.getTime())) return null;
	return date.toLocaleDateString();
}
const serviceRequestFields = computed<ServiceRequestField[]>(() => {
	const sr = serviceRequest.value;
	if (!sr) return [];
	const fields: ServiceRequestField[] = [];
	function add(
		label: string,
		value: string | number | null | undefined,
		href?: string,
	) {
		if (value == null) return;
		const text = String(value).trim();
		if (text === "") return;
		fields.push({ label, value: text, href });
	}
	add("Status", sr.status);
	add("Source", sr.source);
	add("Priority", sr.priority);
	add("Target", sr.target);
	add("SR Number", sr.sr_number);
	add("Assigned Technician", sr.assigned_technician);
	add("Supervisor", sr.supervisor);
	add("Entry Technician", sr.entry_technician);
	add("Scheduled", formatDate(sr.scheduled_date));
	add("Recorded", formatDate(sr.recorded_date));
	add("Closed", formatDate(sr.closed_date));
	add("Due", formatDate(sr.due_date));
	add("Address", sr.request_address_1);
	add("Address 2", sr.request_address_2);
	add("City", sr.request_city);
	add("State", sr.request_state);
	add("Zip", sr.request_zip);
	add("Cross Street", sr.cross_street);
	add("Caller", sr.caller_name);
	add(
		"Caller Phone",
		sr.caller_phone_1,
		sr.caller_phone_1 ? `tel:${sr.caller_phone_1}` : undefined,
	);
	add(
		"Caller Phone 2",
		sr.caller_phone_2,
		sr.caller_phone_2 ? `tel:${sr.caller_phone_2}` : undefined,
	);
	add(
		"Caller Email",
		sr.caller_email,
		sr.caller_email ? `mailto:${sr.caller_email}` : undefined,
	);
	return fields;
});
const serviceRequestNotes = computed<ServiceRequestNote[]>(() => {
	const sr = serviceRequest.value;
	if (!sr) return [];
	const notes: ServiceRequestNote[] = [];
	if (sr.notes_for_technician) {
		notes.push({
			label: "Notes for Technician",
			value: sr.notes_for_technician,
		});
	}
	if (sr.schedule_notes) {
		notes.push({ label: "Schedule Notes", value: sr.schedule_notes });
	}
	return notes;
});
function resourceByCreated(comm: Communication): ResourceStub[] {
	let to_sort: ResourceStub[] = [];
	comm.related.forEach((r: ResourceStub) => to_sort.push(r));
	comm.contact.messages.forEach((m: Message) => {
		to_sort.push({
			...m,
			type: "message",
			uri: "fake-uri",
		});
	});
	return to_sort.sort((a: ResourceStub, b: ResourceStub): number => {
		return b.created.getTime() - a.created.getTime();
	});
}
</script>
